import type React from "react";
import { type ChangeEvent, useEffect, useState } from "react";
import { useIsVerified } from "src/components/email-verification";
import { MainCard } from "src/components/main-card-type.tsx";
import { Button } from "src/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "src/components/ui/card";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import {
  checkCalculatorUsageLimitAndRedirect,
  loadSavedCalculatorData,
  saveCalculatorData,
} from "src/utils/functions";
import { renderInvestmentCard } from "./components/investment-card";
import { renderTooltip } from "./components/tooltip";
import { STATIC, calculate, calculateYield } from "./functions";

interface Investment {
  type: string;
  yieldType: string;
  rate: string;
  months: string;
}

interface Errors {
  investment1: {
    type: boolean;
    yieldType: boolean;
    rate: boolean;
    months: boolean;
  };
  investment2: {
    type: boolean;
    yieldType: boolean;
    rate: boolean;
    months: boolean;
  };
}
const calculatorKey = "comparador-renda-fixa";

export default function ComparadorRendaFixa() {
  const [investment1, setInvestment1] = useState<Investment>({
    type: "",
    yieldType: "",
    rate: "",
    months: "",
  });

  const [investment2, setInvestment2] = useState<Investment>({
    type: "",
    yieldType: "",
    rate: "",
    months: "",
  });

  const [result, setResult] = useState("");
  const [errors, setErrors] = useState<Errors>({
    investment1: { type: false, yieldType: false, rate: false, months: false },
    investment2: { type: false, yieldType: false, rate: false, months: false },
  });
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const isVerified = useIsVerified();

  const handleCalculate = calculate(
    investment1,
    investment2,
    setErrors,
    setShowErrorMessage,
    setResult
  );

  const sanitizeBrlValue = (value: string): string => {

    value = value.replace(/[^\d.,]/g, "");
    value = value.replace(/\./g, ',');

    const parts = value.split(',');
    if (parts.length > 2) {
      value = parts[0] + ',' + parts.slice(1).join('');
    }

    if (parts.length === 2 && parts[1].length > 2) {
      value = parts[0] + ',' + parts[1].slice(0, 2);
    }

    return value;
  };

  const handlePercentageChange = (
    e: ChangeEvent<HTMLInputElement>,
    setInvestment: React.Dispatch<React.SetStateAction<Investment>>,
    investment: Investment
  ) => {
    const formatted = sanitizeBrlValue(e.target.value);
    setInvestment({ ...investment, rate: formatted });
  };

  const handleMonthsChange = (
    e: ChangeEvent<HTMLInputElement>,
    setInvestment: React.Dispatch<React.SetStateAction<Investment>>,
    investment: Investment
  ) => {
    const value = e.target.value.replace(/[^\d]/g, "").slice(0, 5);
    setInvestment({ ...investment, months: value });
  };

  function handleSave() {
    saveCalculatorData(calculatorKey, {
      investment1,
      investment2,
    });
  }

  useEffect(() => {
    const savedData = loadSavedCalculatorData(calculatorKey);
    if (!savedData) return;
    setInvestment1(
      (savedData.investment1 as Investment) || {
        type: "",
        yieldType: "",
        rate: "",
        months: "",
      }
    );
    setInvestment2(
      (savedData.investment2 as Investment) || {
        type: "",
        yieldType: "",
        rate: "",
        months: "",
      }
    );
  }, []);

  return (
    <MainCard className="w-full mx-auto">
      <CardHeader>
        <CardTitle>Comparador de Renda Fixa</CardTitle>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            handleSave();
            checkCalculatorUsageLimitAndRedirect(handleCalculate);
          }}
          className="space-y-4"
        >
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="type1">
                Qual é o tipo de investimento?
                {renderTooltip(
                  "Selecione o tipo de investimento que você deseja comparar"
                )}
              </Label>
              <Select
                value={investment1.type}
                onValueChange={(value) =>
                  setInvestment1({
                    ...investment1,
                    type: value,
                    yieldType:
                      value === "AUPO11" ? "% do cdi" : investment1.yieldType,
                    rate: value === "AUPO11" ? "100" : investment1.rate,
                  })
                }
              >
                <SelectTrigger id="type1">
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  {STATIC.INVESTMENT_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.investment1.type && (
                <span className="text-red-500">*</span>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="type2">
                Você quer comparar com:
                {renderTooltip(
                  "Selecione o segundo tipo de investimento para comparação"
                )}
              </Label>
              <Select
                value={investment2.type}
                onValueChange={(value) =>
                  setInvestment2({
                    ...investment2,
                    type: value,
                    yieldType:
                      value === "AUPO11" ? "% do cdi" : investment2.yieldType,
                    rate: value === "AUPO11" ? "100" : investment2.rate,
                  })
                }
              >
                <SelectTrigger id="type2">
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  {STATIC.INVESTMENT_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.investment2.type && (
                <span className="text-red-500">*</span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>
                A rentabilidade está em
                {renderTooltip(
                  "Escolha como a rentabilidade do investimento é expressa"
                )}
              </Label>
              <div className="flex flex-wrap gap-2">
                {STATIC.YIELD_TYPES.map((type) => (
                  <Button
                    key={type}
                    variant={
                      investment1.yieldType === type ? "default" : "outline"
                    }
                    onClick={() =>
                      setInvestment1({ ...investment1, yieldType: type })
                    }
                    className="text-sm p-2 h-8"
                  >
                    {type}
                  </Button>
                ))}
              </div>
              {errors.investment1.yieldType && (
                <span className="text-red-500">*</span>
              )}
            </div>
            <div className="space-y-2">
              <Label>
                A rentabilidade está em
                {renderTooltip(
                  "Escolha como a rentabilidade do segundo investimento é expressa"
                )}
              </Label>
              <div className="flex flex-wrap gap-2">
                {STATIC.YIELD_TYPES.map((type) => (
                  <Button
                    key={type}
                    variant={
                      investment2.yieldType === type ? "default" : "outline"
                    }
                    onClick={() =>
                      setInvestment2({ ...investment2, yieldType: type })
                    }
                    className="text-sm p-2 h-8"
                  >
                    {type}
                  </Button>
                ))}
              </div>
              {errors.investment2.yieldType && (
                <span className="text-red-500">*</span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="rate1">
                Rentabilidade
                {renderTooltip(
                  "Digite a taxa de rentabilidade do investimento"
                )}
              </Label>
              <Input
                id="rate1"
                placeholder="10,00"
                value={investment1.rate}
                onChange={(e) =>
                  handlePercentageChange(e, setInvestment1, investment1)
                }
              />
              {errors.investment1.rate && (
                <span className="text-red-500">*</span>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="rate2">
                Rentabilidade
                {renderTooltip(
                  "Digite a taxa de rentabilidade do segundo investimento"
                )}
              </Label>
              <Input
                id="rate2"
                placeholder="10,00"
                value={investment2.rate}
                onChange={(e) =>
                  handlePercentageChange(e, setInvestment2, investment2)
                }
              />
              {errors.investment2.rate && (
                <span className="text-red-500">*</span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="months1">
                Tempo de investimento (meses)
                {renderTooltip("Digite o período do investimento em meses")}
              </Label>
              <Input
                id="months1"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="12"
                value={investment1.months}
                onChange={(e) =>
                  handleMonthsChange(e, setInvestment1, investment1)
                }
                maxLength={5}
              />
              {errors.investment1.months && (
                <span className="text-red-500">*</span>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="months2">
                Tempo de investimento (meses)
                {renderTooltip(
                  "Digite o período do segundo investimento em meses"
                )}
              </Label>
              <Input
                id="months2"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="12"
                value={investment2.months}
                onChange={(e) =>
                  handleMonthsChange(e, setInvestment2, investment2)
                }
                maxLength={5}
              />
              {errors.investment2.months && (
                <span className="text-red-500">*</span>
              )}
            </div>
          </div>

          <div className="flex justify-between items-center flex-wrap">
            <p className="text-sm text-gray-500 mt-4">
              Valores base utilizados: CDI {STATIC.CDI}%, IPCA {STATIC.IPCA}%
            </p>
            <Button
              disabled={!isVerified}
              type="submit"
              className="w-full max-w-60"
            >
              Calcular
            </Button>
          </div>
          {showErrorMessage && (
            <p className="text-red-500">Preencha todos os campos</p>
          )}

          {result && (
            <>
              <Card>
                <CardContent className="pt-6">
                  <p
                    dangerouslySetInnerHTML={{
                      __html: result.replace(
                        /\*\*(.*?)\*\*/g,
                        "<strong>$1</strong>"
                      ),
                    }}
                  />
                </CardContent>
              </Card>
              <div className="flex gap-4 flex-wrap">
                {renderInvestmentCard(
                  investment1.type,
                  calculateYield(
                    investment1.type,
                    investment1.yieldType,
                    investment1.rate,
                    Number.parseInt(investment1.months)
                  ),
                  Number.parseInt(investment1.months)
                )}
                {renderInvestmentCard(
                  investment2.type,
                  calculateYield(
                    investment2.type,
                    investment2.yieldType,
                    investment2.rate,
                    Number.parseInt(investment2.months)
                  ),
                  Number.parseInt(investment2.months)
                )}
              </div>
            </>
          )}
        </form>
      </CardContent>
    </MainCard>
  );
}